import type {
  GroupedSubmissionListResponse,
  Product,
  SubmissionImage,
  SubmissionSource,
} from './types';

type MockGroupedSubmission = GroupedSubmissionListResponse['items'][number];

const submittedAt = '2026-05-01T00:00:00Z';

function mockCurrentProduct(id: number, upc: string, name: string, hasImage = false): Product {
  return {
    id,
    upc,
    name,
    hasImage,
    isSeasonal: false,
    createdAt: submittedAt,
    updatedAt: submittedAt,
    lifecycleStatus: 'active',
    displayUpc: upc,
  };
}

function mockSubmissionImage(id: number, submissionId: number): SubmissionImage {
  return {
    id,
    submissionId,
    imagePath: `uploads/submissions/${submissionId}-${id}.jpg`,
    isPrimary: true,
    imageType: 'front',
    uploadedAt: submittedAt,
  };
}

const reviewDefaults = {
  isRingerPackage: false,
  duplicateMatches: [],
  activeApprovedDuplicateMatches: [],
  blockingReasons: [],
};

function mockDatabricksIriSubmission({
  id,
  upc,
  name,
  brand,
  manufacturer,
  packageName,
  category,
  existingProductId,
  conflicts = [],
}: {
  id: number;
  upc: string;
  name: string;
  brand: string;
  manufacturer?: string;
  packageName?: string;
  category?: string;
  existingProductId?: number;
  conflicts?: string[];
}): MockGroupedSubmission {
  const currentProduct = existingProductId
    ? mockCurrentProduct(existingProductId, upc, name, false)
    : null;

  if (currentProduct) {
    currentProduct.brand = brand;
    currentProduct.manufacturer = manufacturer;
    currentProduct.category = category;
    currentProduct.package = packageName;
  }

  return {
    upc,
    submissionCount: 1,
    submissions: [
      {
        id,
        upc,
        name,
        brand,
        manufacturer,
        package: packageName,
        category,
        status: 'pending',
        isRepurpose: false,
        isUpcChangeRequested: Boolean(existingProductId),
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: submittedAt,
        existingProductId,
        images: [],
        attachments: [],
        ...reviewDefaults,
        metadata: {
          databricksPreview: {
            source: 'abpl-enrichment-preview',
            sourceTables: [
              'commercial.product.product',
              'commercial.planogram.gsc_abpl',
              'external.iri.abi_ddm_prod_meta',
            ],
            conflicts,
          },
          iriValidation: {
            status: conflicts.length > 0 ? 'mismatch' : 'match',
            iriBrand: brand,
            iriManufacturer: manufacturer ?? null,
            matchType: 'databricks-preview',
          },
        },
      },
    ],
    hasConflicts: conflicts.length > 0,
    conflicts: conflicts.map((fieldName) => ({
      fieldName,
      currentValue: currentProduct?.[fieldName as keyof typeof currentProduct]
        ? String(currentProduct[fieldName as keyof typeof currentProduct])
        : null,
      conflictingValues: {
        databricks: fieldName,
      },
    })),
    currentProduct,
  } as MockGroupedSubmission;
}

const MOCK_GROUPED_SUBMISSIONS: MockGroupedSubmission[] = [
  mockDatabricksIriSubmission({
    id: 401,
    upc: '1820096178',
    name: "Alexander Keith's Brown Ale 24/12B",
    brand: "Alexander Keith's Brown Ale",
    manufacturer: 'Anheuser-Busch InBev',
    packageName: '24/12B 4x6',
    category: 'Beer',
    existingProductId: 9401,
  }),
  mockDatabricksIriSubmission({
    id: 402,
    upc: '8248813200',
    name: 'Becks 2/169MBR',
    brand: 'Becks',
    manufacturer: 'Anheuser-Busch InBev',
    packageName: '2/169MBR',
    category: 'Beer',
    existingProductId: 9402,
    conflicts: ['name', 'brand', 'manufacturer', 'package', 'wampSegment'],
  }),
  mockDatabricksIriSubmission({
    id: 403,
    upc: '1820026021',
    name: 'Bon Viv SS Classic 1/12C',
    brand: 'Bon Viv SS Classic',
    manufacturer: 'Anheuser-Busch InBev',
    packageName: '1/12C',
    category: 'Hard Beverages',
    existingProductId: 9403,
    conflicts: ['name', 'brand', 'manufacturer', 'package', 'category', 'wampSegment', 'gtin'],
  }),
  mockDatabricksIriSubmission({
    id: 404,
    upc: '85246500606',
    name: 'WYN MGC CTY PAL 6% 24/12 AL CAN 4/6',
    brand: 'WYN MGC CTY PAL',
    manufacturer: 'CBA',
    packageName: '24/12 AL CAN 4/6',
    category: 'BEER',
    existingProductId: 9404,
    conflicts: ['name', 'package'],
  }),
  mockDatabricksIriSubmission({
    id: 405,
    upc: '85753100543',
    name: 'WICKED WEED GARCON DE FERME FARMHOUSE ALE',
    brand: 'WICKED WEED GARCON DE FERME FARMHOUSE ALE',
    manufacturer: 'ANHEUSER-BUSCH INBEV',
    packageName: 'SINGLES 16.9 OZ LNBTL',
    category: 'BEER',
  }),
  mockDatabricksIriSubmission({
    id: 406,
    upc: '76227407142',
    name: 'BASS PALE ALE 5% 12/19.25 OZ NRLN',
    brand: 'BASS PALE ALE',
    manufacturer: 'ABI',
    packageName: '12/19.25 OZ NRLN',
    category: 'BEER',
    existingProductId: 9406,
  }),
  mockDatabricksIriSubmission({
    id: 407,
    upc: '76227443699S',
    name: 'BASS PALE ALE 5% 24/16.9 CAN 6/4 SP TRAY',
    brand: 'BASS PALE ALE',
    manufacturer: 'ABI',
    packageName: '24/16.9 CAN 6/4 SP TRAY',
    category: 'BEER',
    existingProductId: 9407,
  }),
  {
    upc: '012345678901',
    submissionCount: 1,
    submissions: [
      {
        id: 301,
        upc: '012345678901',
        name: 'Michelob Ultra Lime Cactus',
        brand: 'Michelob Ultra',
        status: 'pending',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'user',
        submittedAt: '2026-04-30T14:18:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: mockCurrentProduct(9001, '012345678901', 'Michelob Ultra Lime Cactus'),
  } as MockGroupedSubmission,
  {
    upc: '750000112233',
    submissionCount: 1,
    submissions: [
      {
        id: 302,
        upc: '750000112233',
        name: 'Modelo Oro Shelf Refresh',
        brand: 'Modelo',
        status: 'pending',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: true,
        submissionSource: 'user',
        submittedAt: '2026-04-28T09:42:00Z',
        images: [mockSubmissionImage(11, 302)],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: mockCurrentProduct(9002, '750000112233', 'Modelo Oro Shelf Refresh', true),
  } as MockGroupedSubmission,
  {
    upc: '12345678901',
    submissionCount: 1,
    submissions: [
      {
        id: 303,
        upc: '12345678901',
        name: 'Bud Light 12pk Can',
        brand: 'Bud Light',
        status: 'hold',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: '2026-04-25T16:05:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
        metadata: {
          upcDigitCount: 11,
          iriValidation: {
            status: 'mismatch',
            iriBrand: 'Budweiser',
          },
        },
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: mockCurrentProduct(9003, '12345678901', 'Bud Light 12pk Can'),
  } as MockGroupedSubmission,
  {
    upc: '998877665544',
    submissionCount: 1,
    submissions: [
      {
        id: 304,
        upc: '998877665544',
        name: 'Stella Artois Mini Keg',
        brand: 'Stella Artois',
        status: 'approved',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: '2026-04-20T11:12:00Z',
        reviewedAt: '2026-04-21T15:33:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: null,
  } as MockGroupedSubmission,
  {
    upc: '665544332211',
    submissionCount: 2,
    submissions: [
      {
        id: 305,
        upc: '665544332211',
        name: 'Corona Familiar Glass',
        brand: 'Corona',
        status: 'pending',
        isRepurpose: true,
        isUpcChangeRequested: true,
        isImageOnly: false,
        submissionSource: 'user',
        submittedAt: '2026-05-01T08:10:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
      {
        id: 306,
        upc: '665544332211',
        name: 'Corona Familiar Glass',
        brand: 'Corona',
        status: 'rejected',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: '2026-04-27T18:22:00Z',
        reviewedAt: '2026-04-29T13:05:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: true,
    conflicts: [],
    currentProduct: mockCurrentProduct(9004, '665544332211', 'Corona Familiar Glass'),
  } as MockGroupedSubmission,
];

export function getDevGroupedSubmissions(
  status?: string,
  page: number = 1,
  perPage: number = 50,
  source?: SubmissionSource
): GroupedSubmissionListResponse {
  const filteredItems = MOCK_GROUPED_SUBMISSIONS.filter((group) => {
    const matchesStatus =
      !status || group.submissions.some((submission) => submission.status === status);
    const matchesSource =
      !source || group.submissions.some((submission) => submission.submissionSource === source);
    return matchesStatus && matchesSource;
  });

  const totalUpcs = filteredItems.length;
  const totalSubmissions = filteredItems.reduce((sum, group) => sum + group.submissionCount, 0);
  const totalPages = Math.max(1, Math.ceil(totalUpcs / perPage));
  const startIndex = (page - 1) * perPage;
  const items = filteredItems.slice(startIndex, startIndex + perPage);

  const allSubmissions = MOCK_GROUPED_SUBMISSIONS.flatMap((group) => group.submissions);

  return {
    items,
    totalUpcs,
    totalSubmissions,
    page,
    perPage,
    totalPages,
    pendingCount: allSubmissions.filter((submission) => submission.status === 'pending').length,
    approvedCount: allSubmissions.filter((submission) => submission.status === 'approved').length,
    rejectedCount: allSubmissions.filter((submission) => submission.status === 'rejected').length,
    holdCount: allSubmissions.filter((submission) => submission.status === 'hold').length,
    needsInfoCount: allSubmissions.filter((submission) => submission.status === 'needs_info')
      .length,
    iriNewCount: allSubmissions.filter(
      (submission) =>
        submission.submissionSource === 'iri' &&
        submission.status === 'pending' &&
        !submission.existingProductId
    ).length,
    iriUpdateCount: allSubmissions.filter(
      (submission) =>
        submission.submissionSource === 'iri' &&
        submission.status === 'pending' &&
        Boolean(submission.existingProductId)
    ).length,
    irisImageCount: allSubmissions.filter(
      (submission) => submission.submissionSource === 'iris' && submission.status === 'pending'
    ).length,
    productDataCount: allSubmissions.filter(
      (submission) =>
        submission.submissionSource === 'product_data' && submission.status === 'pending'
    ).length,
    completedCount: allSubmissions.filter((submission) =>
      ['approved', 'rejected'].includes(submission.status)
    ).length,
  };
}
